"use client";

import { useMemo } from "react";
import { usePathname } from "next/navigation";

import { formatPrice, isFreePrice } from "@/lib/format-price";
import type { PriceStory } from "@/lib/prices";
import { regionFromPathname } from "@/lib/region";

type Row = {
  uuid: string;
  name: string;
  cardPrice: string | undefined;
  isFree: boolean;
};

function buildRow(price: PriceStory, region: "ie" | "uk"): Row {
  const c = price.content || {};
  const raw = region === "uk" ? c.price_uk : c.price_ie;
  const free = isFreePrice(raw, c.category);
  const baseName = (region === "uk" && c.name_uk ? c.name_uk : c.name) || price.name;
  const name = c.asterisk ? `${baseName}${c.asterisk}` : baseName;
  return {
    uuid: price.uuid,
    name,
    cardPrice: free ? "FREE" : formatPrice(raw, region),
    isFree: free,
  };
}

export default function TestingPriceListView({
  title,
  prices,
  footnote,
}: {
  title?: string;
  prices: PriceStory[];
  footnote?: string;
}) {
  const pathname = usePathname();
  const region = regionFromPathname(pathname);

  // Hide prices missing a value for the current region.
  const rows = useMemo(
    () =>
      prices
        .map((price) => buildRow(price, region))
        .filter((row) => Boolean(row.cardPrice)),
    [prices, region],
  );

  if (rows.length === 0) return null;

  const renderItem = (row: Row) => (
    <li
      key={row.uuid}
      className="flex items-center justify-between gap-4 rounded-[8px] bg-white p-5 md:rounded-[16px] md:p-6 lg:rounded-[20px] lg:p-9"
    >
      <p className="text-h5 font-semibold leading-[1.2] text-black md:text-h4 lg:text-h4 lg:leading-[1.08]">
        {row.name}
      </p>
      {row.cardPrice && (
        <p
          className={`shrink-0 text-h5 font-extrabold leading-[1.2] lg:text-[28px] lg:leading-[1.054] lg:tracking-[0.02em] ${
            row.isFree ? "text-forest-01" : "text-purple-03"
          }`}
        >
          {row.cardPrice}
        </p>
      )}
    </li>
  );

  return (
    <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-10 lg:py-[77px]">
      {title && (
        <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05">
          {title}
        </h2>
      )}

      {/* Cards are uniform height (name + price), so a plain grid suffices. */}
      <ul className="mt-6 grid grid-cols-1 gap-2 md:mt-12 md:grid-cols-2 md:gap-4 lg:mt-16 lg:gap-[23px]">
        {rows.map(renderItem)}
      </ul>

      {footnote && (
        <p className="mt-6 whitespace-pre-line text-button leading-[1.56] text-[#595959] md:mt-8 lg:mt-10">
          {footnote}
        </p>
      )}
    </div>
  );
}
